import { DateTime } from 'luxon';
import last from 'lodash/last';
import statusTitles from '@/js/enums/RequestStatusTitle';
import RequestStatusType from '@/js/enums/RequestStatusType';
import RequestStateType from '@/js/enums/RequestStateType';
import RequestHistoryActionType from '@/js/enums/RequestHistoryActionType';
import RequestStatusColor from '@/js/enums/RequestStatusColor';
import RequestCommentType from '@/js/enums/RequestCommentType';
import { DateLainer } from '@/js/vue/mixins/DateLainer';
import { pluralizeRu } from '@/js/requestStorePagination';

const STATUS_CLASS = {
    [RequestStatusType.WORKUP]: 'work',
    [RequestStatusType.FINISH]: 'done',
    [RequestStatusType.REWORK]: 'wait',
    [RequestStatusType.PAUSED]: 'wait',
    [RequestStatusType.DENIED]: 'wait',
    [RequestStatusType.CLOSED]: 'closed',
};

const OVERDUE_COLOR = '#f52222';
// REWORK сюда не входит: у него свой ранний return в deadlineView() (срок заморожен, пока
// заявка ждёт ответа автора) — этот список отвечает только за "актуальный отсчёт по deadline".
const ACTIVE_DEADLINE_STATUSES = [RequestStatusType.WORKUP];
const DAY_MS = 24 * 60 * 60 * 1000;

const FLEX_COLUMN_MIN = 180;

// Горизонтальный паддинг ячейки (10px + 10px). От размера текста он не зависит.
const CELL_PADDING = 20;

// Ширины колонок записаны в px стандартного размера текста. Текст в ячейках растёт
// и уменьшается вместе с персональной настройкой (--text-scale, см. js/textSize.js),
// паддинги — нет, поэтому масштабируется только текстовая часть: иначе в крупном
// режиме значения не помещались бы в колонку, а в компактном колонка ужималась бы
// сильнее своего текста. cells — сколько ячеек (и их паддингов) входит в длину.
//
// Числа остаются в px стандартного режима (gridMinWidth, stickyOffsets), а в CSS
// уходит calc(): смена режима перестраивает таблицу без пересчёта в JS.
export function scaledLength(px, cells = 1) {
    const fixed = CELL_PADDING * cells;

    return `calc(${px - fixed}px * var(--text-scale, 1) + ${fixed}px)`;
}

export function gridTemplate(columns) {
    return columns
        .map((column) => (column.width ? scaledLength(column.width) : `minmax(${scaledLength(FLEX_COLUMN_MIN)}, 1fr)`))
        .join(' ');
}

// Минимальная ширина сетки — сумма треков grid-template-columns. Ниже неё колонки
// не сжимаются, поэтому этим же значением задаётся ширина шапки, строк и футера,
// чтобы при горизонтальной прокрутке они не обрывались по краю видимой области.
export function gridMinWidth(columns) {
    return columns.reduce((sum, column) => sum + (column.width || FLEX_COLUMN_MIN), 0);
}

// Та же ширина для CSS — с учётом размера текста.
export function gridMinWidthCss(columns) {
    return scaledLength(gridMinWidth(columns), columns.length);
}

// Смещение закреплённой колонки для CSS: перед колонкой с номером index ровно index ячеек.
export function stickyOffsetCss(offset, index) {
    return offset === null ? null : scaledLength(offset, index);
}

// При горизонтальной прокрутке закрепляются колонки с флагом pinned. Смещение каждой —
// сумма ширин предыдущих; у остальных null, то есть «не закреплять».
//
// Считаем не все pinned-колонки, а только идущую с начала строки непрерывную цепочку:
// колонка, перед которой есть прокручиваемая, приклеилась бы не к краю. Порядок держит
// pinnedFirst в tableColumns, эта проверка — страховка на случай его обхода.
export function lastStickyIndex(columns) {
    let index = -1;

    while (columns[index + 1] && columns[index + 1].pinned) {
        index += 1;
    }

    return index;
}

// Гибкое «Описание» считается по FLEX_COLUMN_MIN, и это точное значение, а не оценка:
// сетке задан min-width в gridMinWidth, где эта колонка учтена теми же 180px. Горизонтальная
// прокрутка возможна только когда скролпорт уже min-width, а тогда свободного места нет
// и minmax(180px, 1fr) раскрывается ровно в 180px. Если же места хватает, прокручивать
// нечего и закрепление всё равно ничего не двигает.
export function stickyOffsets(columns) {
    const lastIndex = lastStickyIndex(columns);
    if (lastIndex === -1) return columns.map(() => null);

    let offset = 0;

    return columns.map((column, index) => {
        if (index > lastIndex) return null;

        const current = offset;
        offset += column.width || FLEX_COLUMN_MIN;

        return current;
    });
}

export function isNewRequest(request) {
    return (
        request.status === RequestStatusType.WORKUP &&
        !request.history.some((story) =>
            [
                RequestHistoryActionType.CLOSED,
                RequestHistoryActionType.DENIED,
                RequestHistoryActionType.PAUSED,
                RequestHistoryActionType.REWORK,
                RequestHistoryActionType.WORKUP,
                RequestHistoryActionType.FINISH,
            ].includes(story.action),
        ) &&
        !RequestStateType.isFatal(request)
    );
}

export function unreadCount(request, accountId) {
    const involved =
        request.authorId === accountId ||
        request.executorId === accountId ||
        (request.observers || []).some((observer) => observer.id === accountId);
    if (!involved) return 0;

    const lastSeen = (request.lastSeenComments || []).find((comment) => comment.userId === accountId);
    if (!lastSeen) return (request.comments || []).length;

    return (request.comments || []).filter((comment) => comment.id > lastSeen.commentId).length;
}

function commentText(comment) {
    if (!comment) return '';
    if (comment.text) return comment.text.replace(/<br \/>|<span class='chat-text-color-blue'>|<\/span>/g, ' ');
    if (comment.files && comment.files.length) return `Документ: ${last(comment.files).name}`;
    return '';
}

// Текст самого обращения — не последний комментарий, а именно FIRST_COMMENT: он создаётся
// ровно один раз, атомарно с заявкой (RequestService::create()), и это единственное место
// во всём проекте, где комментарий этого типа вообще создаётся — гарантия, а не эвристика
// по порядку/автору. Тем же критерием уже пользуется экспорт в Excel
// (RequestService::getRequestExportDescription()) — после этой правки список и выгрузка
// наконец показывают одно и то же, а не расходятся.
export function description(request) {
    const comments = request.comments || [];
    const firstComment = comments.find((comment) => comment.type === RequestCommentType.FIRST_COMMENT);

    return commentText(firstComment);
}

// Последний ОТВЕТ человека — только RequestCommentType.COMMENT. Не FIRST_COMMENT (это и есть
// «Описание» — дублировать его в соседней колонке нельзя, читалось бы как сбой отображения:
// пока никто не ответил, здесь прочерк). Не SYSTEM/PAUSED/SOLUTION — у них шаблonный,
// автосгенерированный текст, даже когда внутри спрятана человеческая причина (см. обсуждение
// в задаче); включаем только заведомо человеческий тип, а не перечисляем системные поимённо.
//
// В бэклог, не в эту правку: SOLUTION с текстом «Решение: …», написанным исполнителем при
// завершении — сейчас тоже отсеивается, и у завершённых обращений колонка показывает более
// раннюю переписку. Разобраться, стоит ли считать SOLUTION человеческим для этой колонки.
//
// Максимум по id одним проходом, без .sort(): request.comments — проп компонента, ни
// мутировать, ни даже клонировать весь массив ради одного элемента не нужно.
export function lastMessage(request) {
    const comments = request.comments || [];
    let latest = null;

    for (const comment of comments) {
        if (comment.type !== RequestCommentType.COMMENT) continue;
        if (!latest || comment.id > latest.id) latest = comment;
    }

    return commentText(latest) || '—';
}

export function statusView(request) {
    if (isNewRequest(request)) return { title: 'Новое', className: 'new' };
    return { title: statusTitles[request.status], className: STATUS_CLASS[request.status] };
}

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;

// Собственный форматтер вместо luxon toRelative: для «просрочено на 5 дней» пришлось бы
// отрезать от готовой строки слово «назад», а это ломается при смене локали.
export function humanizeDuration(ms) {
    const abs = Math.abs(ms);

    if (abs < MINUTE_MS) return 'меньше минуты';

    if (abs < HOUR_MS) {
        const minutes = Math.floor(abs / MINUTE_MS);
        return `${minutes} ${pluralizeRu(minutes, 'минута', 'минуты', 'минут')}`;
    }

    if (abs < DAY_MS) {
        const hours = Math.floor(abs / HOUR_MS);
        return `${hours} ${pluralizeRu(hours, 'час', 'часа', 'часов')}`;
    }

    const days = Math.floor(abs / DAY_MS);
    return `${days} ${pluralizeRu(days, 'день', 'дня', 'дней')}`;
}

const DIM = { text: '—', className: 'dim', title: '' };

function toUtcMillis(date) {
    return DateTime.fromSQL(date, { zone: DateLainer.SERVER_TIMEZONE }).toMillis();
}

export function deadlineView(request, toLocalDate, { now = Date.now() } = {}) {
    if (request.status === RequestStatusType.PAUSED) {
        // Дата возобновления лежит в самой заявке (resumeAt): отдельной записи о паузе
        // с её собственным статусом больше нет.
        const resumeAt = request.resumeAt;
        if (!resumeAt) return DIM;

        const left = toUtcMillis(resumeAt) - now;
        const text = left > 0 ? `возобновится через ${humanizeDuration(left)}` : 'возобновится скоро';

        // Возобновление в пределах суток красится тем же классом, что и подходящий срок:
        // колонка сообщает «событие вот-вот», а какое именно — сказано текстом рядом.
        // Отдельный оттенок только под паузу ничего бы не различал, а палитру бы расширил.
        return { text, className: left < DAY_MS ? 'soon' : 'dim', title: toLocalDate(resumeAt) };
    }

    // Срок заморожен так же, как при паузе (RequestService::restoreDeadlineAfterPause
    // переиспользует один и тот же механизм для REWORK и PAUSED) — но, в отличие от паузы,
    // известной даты возобновления тут нет: автор может ответить когда угодно. Поэтому вместо
    // отсчёта или "просрочено" — нейтральный статус ожидания; сам deadline остаётся только
    // в подсказке, для тех, кому интересна замороженная дата.
    if (request.status === RequestStatusType.REWORK) {
        return { text: 'Ждём ответа автора', className: 'dim', title: toLocalDate(request.deadline) };
    }

    if (!ACTIVE_DEADLINE_STATUSES.includes(request.status)) return DIM;

    const left = toUtcMillis(request.deadline) - now;
    const title = toLocalDate(request.deadline);

    if (left <= 0) return { text: `просрочено на ${humanizeDuration(left)}`, className: 'over', title };

    return { text: `через ${humanizeDuration(left)}`, className: left < DAY_MS ? 'soon' : '', title };
}

export function avatarUrl(user) {
    return user && user.photo;
}

export function initials(name) {
    if (!name) return '';
    return name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0].toUpperCase())
        .join('');
}

// Полоса слева от № заявки: цвет статуса, как в прежних видах списка. Исключение —
// заявка в работе с истёкшим или близким сроком: она подсвечивается красным.
export function stateColor(request) {
    const type = RequestStateType.type(request);
    const isOverdue = type === RequestStateType.WARN || type === RequestStateType.FATAL;

    if (request.status === RequestStatusType.WORKUP && isOverdue) return OVERDUE_COLOR;

    return RequestStatusColor[request.status];
}
